import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Landing.css';
import DemoRequestForm from '../components/DemoRequestForm';

function Landing() {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const companyListRef = useRef(null);
    const [showBooking, setShowBooking] = useState(false);

    useEffect(() => {
        // Smooth scrolling for anchor links
        const handleClick = (e) => {
            if (e.target.tagName === 'A' && e.target.getAttribute('href')?.startsWith('#')) {
                e.preventDefault();
                const targetId = e.target.getAttribute('href').substring(1);
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }
            }
        };

        document.addEventListener('click', handleClick);

        // Company list pause on hover
        const companyList = companyListRef.current;
        if (companyList) {
            const handleMouseEnter = () => {
                companyList.style.animationPlayState = 'paused';
            };
            const handleMouseLeave = () => {
                companyList.style.animationPlayState = 'running';
            };

            companyList.addEventListener('mouseenter', handleMouseEnter);
            companyList.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                companyList.removeEventListener('mouseenter', handleMouseEnter);
                companyList.removeEventListener('mouseleave', handleMouseLeave);
            };
        }

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, []);

    useEffect(() => {
        // Fade-in animation on scroll
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('fade-in');
                }
            });
        }, observerOptions);

        document.querySelectorAll('.feature-card, .demo-feature').forEach(el => {
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        // Video error fallback
        const video = videoRef.current;
        if (video) {
            const handleError = (e) => {
                console.error('Video error:', e);
                console.error('Video source:', video.currentSrc);
                const videoContainer = video.parentElement;
                if (videoContainer) {
                    videoContainer.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                }
            };
            
            const handleLoadStart = () => {
                console.log('Video loading started...');
            };
            
            const handleCanPlay = () => {
                console.log('Video can play');
                video.setAttribute('data-loaded', 'true');
                video.play().catch(e => console.error('Autoplay error:', e));
            };
            
            const handleLoadedData = () => {
                console.log('Video loaded');
                video.setAttribute('data-loaded', 'true');
            };
            
            video.addEventListener('error', handleError);
            video.addEventListener('loadstart', handleLoadStart);
            video.addEventListener('canplay', handleCanPlay);
            video.addEventListener('loadeddata', handleLoadedData);

            // Pause video when not in viewport to save resources
            const handleScroll = () => {
                const rect = video.getBoundingClientRect();
                const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
                
                if (isVisible && video.paused && video.readyState >= 2) {
                    video.play().catch(() => {});
                } else if (!isVisible && !video.paused) {
                    video.pause();
                }
            };

            window.addEventListener('scroll', handleScroll);
            
            return () => {
                video.removeEventListener('error', handleError);
                video.removeEventListener('loadstart', handleLoadStart);
                video.removeEventListener('canplay', handleCanPlay);
                video.removeEventListener('loadeddata', handleLoadedData);
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, []);

    const handleGetStarted = () => {
        setShowBooking(true);
    };

    const handleDemo = () => {
        setShowBooking(true);
    };

    const handleLogin = () => {
        navigate('/login');
    };

    return (
        <div className="landing-page">
            {/* Header */}
            <header className="landing-header">
                <div className="header-left">
                    <span className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ cursor: 'pointer' }}>ACO Insights</span>
                </div>
                <nav className="header-center">
                    <a href="#features">Features</a>
                    <a href="#how-it-works">Workflow</a>
                    <a href="#security">Security</a>
                </nav>
                <div className="header-right">
                    <a href="#demo" onClick={handleDemo}>Demo</a>
                    <a href="#login" onClick={handleLogin}>Log In</a>
                </div>
            </header>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="video-container">
                    <video 
                        ref={videoRef}
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        preload="metadata"
                        className="hero-video"
                    >
                        <source src="https://storage.googleapis.com/acoinsights-videos/10sec.mp4" type="video/mp4" />
                    </video>
                    <div className="video-overlay"></div>
                </div>
                
                <div className="hero-content">
                    <h1 className="hero-title">Transform Healthcare Analytics</h1>
                    <h2 className="hero-tagline">Elevate Your ACO Performance</h2>
                    <p className="hero-subtitle">
                        Empowering ACOs with data-driven insights<br />
                        for better patient outcomes
                    </p>
                    <button onClick={handleGetStarted} className="btn-primary">Book Demo</button>
                </div>

                {/* Company Bar at Bottom of Hero */}
                <div className="hero-companies-overlay">
                    <span className="company-label-overlay">OUR DNA</span>
                    <div className="company-container-overlay">
                        <div className="company-list" ref={companyListRef}>
                            <span className="company-item">FIS</span>
                            <span className="company-item">Wolters Kluwer</span>
                            <span className="company-item">Charles Schwab</span>
                            <span className="company-item">Compuware</span>
                            <span className="company-item">UNC Chapel Hill</span>
                            <span className="company-item">Society of Actuaries</span>
                            <span className="company-item">FIS</span>
                            <span className="company-item">Wolters Kluwer</span>
                            <span className="company-item">Charles Schwab</span>
                            <span className="company-item">Compuware</span>
                            <span className="company-item">UNC Chapel Hill</span>
                            <span className="company-item">Society of Actuaries</span>
                            <span className="company-item">FIS</span>
                            <span className="company-item">Wolters Kluwer</span>
                            <span className="company-item">Charles Schwab</span>
                            <span className="company-item">Compuware</span>
                            <span className="company-item">UNC Chapel Hill</span>
                            <span className="company-item">Society of Actuaries</span>
                            <span className="company-item">FIS</span>
                            <span className="company-item">Wolters Kluwer</span>
                            <span className="company-item">Charles Schwab</span>
                            <span className="company-item">Compuware</span>
                            <span className="company-item">UNC Chapel Hill</span>
                            <span className="company-item">Society of Actuaries</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section with Videos */}
            <section className="features-video-section" id="features">
                <div className="services-header">
                    <h2 className="services-title">Some of Our Services</h2>
                </div>
                <div className="container-fluid">
                    {/* Feature 1: Population Analytics */}
                    <div className="feature-video-row">
                        <div className="feature-video-text">
                            <h2 className="feature-video-title">Population Analytics and Inpatient Performance</h2>
                            <p className="feature-video-description">
                                Attributed population view with demographics, risk scores, and chronic conditions. Readmissions analysis and inpatient length-of-stay tracking.
                            </p>
                            <div className="feature-video-outcome">
                                <span className="outcome-label">OUTCOME:</span>
                                <p>Identify which patients and conditions are driving cost and utilization, while controlling inpatient utilization and reducing preventable readmissions</p>
                            </div>
                        </div>
                        <div className="feature-video-container">
                            <video 
                                autoPlay 
                                loop 
                                muted 
                                playsInline
                                className="feature-video"
                            >
                                <source src="https://storage.googleapis.com/acoinsights-videos/patient.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>

                    {/* Feature 2: ER and MSSP */}
                    <div className="feature-video-row reverse">
                        <div className="feature-video-text">
                            <h2 className="feature-video-title">Emergency Department Management and MSSP Actuarial Projections</h2>
                            <p className="feature-video-description">
                                ED utilization tracking and avoidable visit identification. CMS-aligned methodology with MSSP monthly projections.
                            </p>
                            <div className="feature-video-outcome">
                                <span className="outcome-label">OUTCOME:</span>
                                <p>Reduce preventable emergency visits and manage acute care costs, while understanding savings or losses as the year unfolds — no end-of-year surprises</p>
                            </div>
                        </div>
                        <div className="feature-video-container">
                            <video 
                                autoPlay 
                                loop 
                                muted 
                                playsInline
                                className="feature-video"
                            >
                                <source src="https://storage.googleapis.com/acoinsights-videos/ER.TCOC.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>

                    {/* Feature 3: Post-Acute Analytics */}
                    <div className="feature-video-row">
                        <div className="feature-video-text">
                            <h2 className="feature-video-title">Post-Acute Analytics</h2>
                            <p className="feature-video-description">
                                SNF, HHA, and Hospice utilization and cost insights
                            </p>
                            <div className="feature-video-outcome">
                                <span className="outcome-label">OUTCOME:</span>
                                <p>Control and manage post-acute care utilization and cost</p>
                            </div>
                        </div>
                        <div className="feature-video-container">
                            <video 
                                autoPlay 
                                loop 
                                muted 
                                playsInline
                                className="feature-video"
                            >
                                <source src="https://storage.googleapis.com/acoinsights-videos/snf,hha,hos.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>

                    {/* Feature 4: AI Insights */}
                    <div className="feature-video-row reverse">
                        <div className="feature-video-text">
                            <h2 className="feature-video-title">AI-Powered Insights</h2>
                            <p className="feature-video-description">
                                Conversational AI that understands your data, answers complex questions, and surfaces hidden patterns instantly
                            </p>
                            <div className="feature-video-outcome">
                                <span className="outcome-label">OUTCOME:</span>
                                <p>Transform hours of analysis into seconds with intelligent data exploration</p>
                            </div>
                        </div>
                        <div className="feature-video-container">
                            <video 
                                autoPlay 
                                loop 
                                muted 
                                playsInline
                                className="feature-video"
                            >
                                <source src="https://storage.googleapis.com/acoinsights-videos/ai.part.mp4" type="video/mp4" />
                            </video>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works-section" id="how-it-works">
                <div className="container">
                    <h2 className="how-it-works-title">How It Works</h2>
                    <p className="how-it-works-subtitle">Get started in three simple steps</p>
                    
                    <div className="steps-container">
                        <div className="step-card">
                            <div className="step-icon">⇪</div>
                            <div className="step-number">Step 1</div>
                            <h3 className="step-title">Provide Your CCLF Files</h3>
                            <p className="step-description">Upload your CMS Claims and Claims Line Feed (CCLF) files securely to our platform</p>
                        </div>

                        <div className="step-arrow">→</div>

                        <div className="step-card">
                            <div className="step-icon">⚡</div>
                            <div className="step-number">Step 2</div>
                            <h3 className="step-title">See Instant Insights</h3>
                            <p className="step-description">Our platform automatically analyzes your data, generates comprehensive dashboards, actuarial projections, and AI-powered insights</p>
                        </div>

                        <div className="step-arrow">→</div>

                        <div className="step-card">
                            <div className="step-icon">$</div>
                            <div className="step-number">Step 3</div>
                            <h3 className="step-title">Drive Savings</h3>
                            <p className="step-description">Make data-driven decisions to reduce costs and improve patient outcomes</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Security Section */}
            <section className="security-section" id="security">
                <div className="container">
                    <h2 className="security-title">Enterprise-Grade Security & Compliance</h2>
                    <p className="security-subtitle">Your data security is our top priority. We maintain the highest standards of protection and compliance.</p>
                    
                    <div className="security-grid">
                        <div className="security-card">
                            <div className="security-icon">⊕</div>
                            <h3>HIPAA Compliant</h3>
                            <p>Fully compliant with HIPAA regulations for protected health information (PHI)</p>
                        </div>
                        
                        <div className="security-card">
                            <div className="security-icon">✓</div>
                            <h3>SOC 2 Type II</h3>
                            <p>Independently audited and certified for security, availability, and confidentiality</p>
                        </div>
                        
                        <div className="security-card">
                            <div className="security-icon">⚿</div>
                            <h3>End-to-End Encryption</h3>
                            <p>256-bit AES encryption for data at rest and TLS 1.3 for data in transit</p>
                        </div>
                        
                        <div className="security-card">
                            <div className="security-icon">⚷</div>
                            <h3>Access Controls</h3>
                            <p>Role-based access control (RBAC) and multi-factor authentication (MFA)</p>
                        </div>
                        
                        <div className="security-card">
                            <div className="security-icon">≡</div>
                            <h3>Audit Logging</h3>
                            <p>Comprehensive audit trails for all data access and system activities</p>
                        </div>
                        
                        <div className="security-card">
                            <div className="security-icon">↻</div>
                            <h3>Automated Backups</h3>
                            <p>Daily encrypted backups with 99.9% uptime guarantee</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-bottom">
                        <div className="footer-bottom-content">
                            <p>&copy; 2026 ACO Insights. All rights reserved.</p>
                            <div className="footer-links">
                                <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Home</a>
                                <span className="separator">•</span>
                                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                <span className="separator">•</span>
                                <a href="/privacy-policy#contact" target="_blank" rel="noopener noreferrer">Contact</a>
                                <span className="separator">•</span>
                                <a href="#demo" onClick={(e) => { e.preventDefault(); setShowBooking(true); }}>Demo</a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Demo Request Form Modal */}
            {showBooking && <DemoRequestForm onClose={() => setShowBooking(false)} />}
        </div>
    );
}

export default Landing;
